/* PCIS — Cache Management (route /supply/pcis-health/cache).
 *
 * Inspect and evict PCIS's Redis caches. The catalog lists both layers (Spring @Cacheable caches and
 * the direct-Redis namespaces — auth tokens, serviceability block, pickup locations, AWB pool, …);
 * pick one to list its keys with remaining TTL, optionally reveal values, delete a single key, or
 * purge the whole namespace by glob.
 *
 * All calls go to ProShip /api/pcis-admin/cache/* (which proxies PCIS with the server-side admin key).
 * There is NO flush-all — "purge everything" is a pcis:* glob delete, done per-namespace here.
 * Values (incl. live courier tokens) come back in clear text, so the "Show values" toggle is off by
 * default. Deletes hit shared Redis (all clients), so both delete actions confirm first.
 */
import React, { useMemo, useState } from 'react'
import { Box, Stack, Button, Typography, IconButton, Tooltip, Chip, Switch, FormControlLabel, Alert } from '@mui/material'
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import PageTitle from '../../../components/PageTitle'
import { PageHeader, ConfirmDialog, TableSkeleton, LoadErrorPanel, useToast } from '../components'
import ProzoDataGrid from '../../../common/ProzoDataGrid'
import {
  usePcisCacheCatalogQuery,
  useLazyPcisCacheKeysQuery,
  useLazyPcisCacheEntryQuery,
  useDeletePcisCacheByPatternMutation,
} from '../../../redux/supplyApiSlice'

const fmtTtl = (s) => {
  if (s === -1 || s === '-1') return 'no expiry'
  if (s === -2 || s === '-2' || s === null || s === undefined) return 'gone'
  const n = Number(s)
  if (Number.isNaN(n)) return String(s)
  if (n < 90) return `${n}s`
  if (n < 5400) return `${Math.round(n / 60)}m`
  return `${(n / 3600).toFixed(1)}h`
}

/* Catalog {spring_caches:{}, direct_redis:{}} → one flat, rendered list. */
function flattenCatalog(catalog) {
  const out = []
  const add = (group, obj) => Object.keys(obj || {}).forEach((name) => {
    const m = obj[name] || {}
    out.push({ id: `${group}:${name}`, name, group, pattern: m.pattern, entries: m.approx_entries, description: m.description })
  })
  add('Spring', catalog && catalog.spring_caches)
  add('Direct-Redis', catalog && catalog.direct_redis)
  return out
}

export default function PcisCacheManage() {
  const toast = useToast()
  const { data: catalog, isLoading, isError, refetch } = usePcisCacheCatalogQuery()

  const [selected, setSelected] = useState(null)          // {name, pattern, group}
  const [showValues, setShowValues] = useState(false)
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(20)
  const [confirm, setConfirm] = useState(null)            // {title, body, danger, onOk}
  const [entryView, setEntryView] = useState(null)        // {key, value, ttl} shown inline

  const [loadKeys, keysState] = useLazyPcisCacheKeysQuery()
  const [loadEntry] = useLazyPcisCacheEntryQuery()
  const [deleteByPattern, { isLoading: deleting }] = useDeletePcisCacheByPatternMutation()

  const namespaces = useMemo(() => flattenCatalog(catalog), [catalog])
  const keysData = keysState.data || {}
  const keyRows = keysData.keys || []

  function openNamespace(ns, values = showValues) {
    setSelected(ns)
    setEntryView(null)
    setPage(0)
    loadKeys({ prefix: ns.pattern, limit: (catalog && catalog.max_scan) || 5000, values })
  }

  function closeKeys() {
    setSelected(null)
    setEntryView(null)
    setPage(0)
  }

  async function refreshCatalog() {
    try {
      await refetch()
      if (selected) openNamespace(selected)   // also re-scan the open namespace's keys
      toast('Catalog refreshed.', 'success')
    } catch (e) {
      toast('Refresh failed.', 'error')
    }
  }

  function toggleValues(next) {
    setShowValues(next)
    if (selected) openNamespace(selected, next)
  }

  async function viewValue(key) {
    try {
      const res = await loadEntry(key).unwrap()
      setEntryView({ key, value: res.value, ttl: res.ttl_seconds })
    } catch (e) {
      toast('Could not read the value.', 'error')
    }
  }

  function askDeleteKey(key) {
    setConfirm({
      title: 'Delete this cache entry?',
      body: key,
      danger: true,
      onOk: async () => {
        try {
          const r = await deleteByPattern(key).unwrap()
          toast(`Deleted ${r?.deleted ?? 0} key(s).`, 'success')
          if (selected) openNamespace(selected)
        } catch (e) { toast(e?.message || 'Delete failed.', 'error') }
        setConfirm(null)
      },
    })
  }

  function askPurgeNamespace() {
    if (!selected) return
    setConfirm({
      title: `Purge namespace "${selected.name}"?`,
      body: `Deletes every key matching ${selected.pattern}. This is shared Redis — it affects ALL clients. Entries repopulate on next use.`,
      danger: true,
      onOk: async () => {
        try {
          const r = await deleteByPattern(selected.pattern).unwrap()
          toast(`Purged ${r?.deleted ?? 0} key(s) from ${selected.name}.`, 'success')
          openNamespace(selected)
        } catch (e) { toast(e?.message || 'Purge failed.', 'error') }
        setConfirm(null)
      },
    })
  }

  const catalogColumns = [
    { field: 'name', headerName: 'Cache', flex: 1.3, minWidth: 200,
      renderCell: (p) => <b>{p.row.name}</b> },
    { field: 'group', headerName: 'Layer', width: 130,
      renderCell: (p) => <Chip size='small' variant='outlined' label={p.row.group} /> },
    { field: 'entries', headerName: 'Entries', width: 100 },
    { field: 'pattern', headerName: 'Key pattern', flex: 1.4, minWidth: 220,
      renderCell: (p) => <code style={{ fontSize: 12, opacity: 0.8 }}>{p.row.pattern}</code> },
    { field: 'actions', headerName: '', width: 120, sortable: false, filterable: false,
      renderCell: (p) => <Button size='small' onClick={() => openNamespace(p.row)}>View keys</Button> },
  ]

  const keyColumns = [
    { field: 'key', headerName: 'Key', flex: 2, minWidth: 320,
      renderCell: (p) => <code style={{ fontSize: 12 }}>{p.row.key}</code> },
    { field: 'ttl_seconds', headerName: 'TTL', width: 110, renderCell: (p) => fmtTtl(p.row.ttl_seconds) },
    ...(showValues ? [{ field: 'value', headerName: 'Value', flex: 1.5, minWidth: 200,
      renderCell: (p) => <span style={{ fontSize: 12, wordBreak: 'break-all' }}>{p.row.value}</span> }] : []),
    { field: 'actions', headerName: 'Actions', width: 110, sortable: false, filterable: false,
      renderCell: (p) => (
        <Stack direction='row' spacing={0.5}>
          {!showValues && (
            <Tooltip title='View value'>
              <IconButton size='small' onClick={() => viewValue(p.row.key)}><VisibilityOutlinedIcon fontSize='small' /></IconButton>
            </Tooltip>
          )}
          <Tooltip title='Delete key'>
            <IconButton size='small' color='error' onClick={() => askDeleteKey(p.row.key)}><DeleteOutlineOutlinedIcon fontSize='small' /></IconButton>
          </Tooltip>
        </Stack>
      ) },
  ]

  return (
    <>
      <PageTitle title='PCIS · Cache' />
      <PageHeader
        title='Cache Management'
        sub='Inspect PCIS Redis caches and evict entries. Shared Redis — deletes affect all clients; caches repopulate on next use.'
        actions={<Button size='small' variant='outlined' startIcon={<RefreshOutlinedIcon />} onClick={refreshCatalog}>Refresh catalog</Button>}
      />

      {isLoading ? <TableSkeleton /> : isError ? <LoadErrorPanel onRetry={refetch} /> : (
        <>
          <Typography variant='subtitle2' sx={{ mb: 1 }}>Caches</Typography>
          <Box sx={{ mb: 3 }}>
            <ProzoDataGrid
              columns={catalogColumns}
              rows={namespaces}
              getRowId={(r) => r.id}
              rowCount={namespaces.length}
              paginationMode='client'
              page={0}
              setPage={() => {}}
              sizePerPage={100}
              setSizePerPage={() => {}}
              hideFooterPagination
            />
          </Box>

          {selected && (
            <>
              <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant='subtitle2'>
                  Keys in <b>{selected.name}</b> <code style={{ fontSize: 12, opacity: 0.7 }}>{selected.pattern}</code>
                </Typography>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <FormControlLabel
                    control={<Switch size='small' checked={showValues} onChange={(e) => toggleValues(e.target.checked)} />}
                    label='Show values'
                  />
                  <Button size='small' color='error' variant='outlined' startIcon={<DeleteSweepOutlinedIcon />}
                    onClick={askPurgeNamespace} disabled={deleting}>Purge namespace</Button>
                  <Button size='small' variant='text' startIcon={<CloseOutlinedIcon />} onClick={closeKeys}>Close</Button>
                </Stack>
              </Stack>

              {showValues && (
                <Alert severity='warning' sx={{ mb: 1 }}>
                  Values are shown in clear text and may include live courier tokens / API keys.
                </Alert>
              )}
              {keysData.capped && (
                <Alert severity='info' sx={{ mb: 1 }}>Showing the first {keyRows.length} keys (scan capped).</Alert>
              )}

              {keysState.isFetching ? <TableSkeleton /> : (
                <Box>
                  <ProzoDataGrid
                    columns={keyColumns}
                    rows={keyRows}
                    getRowId={(r) => r.key}
                    rowCount={keyRows.length}
                    paginationMode='client'
                    page={page}
                    setPage={setPage}
                    sizePerPage={sizePerPage}
                    setSizePerPage={setSizePerPage}
                    pagination
                  />
                </Box>
              )}

              {entryView && (
                <Alert severity='info' sx={{ mt: 1 }} onClose={() => setEntryView(null)}>
                  <div><b>{entryView.key}</b> · TTL {fmtTtl(entryView.ttl)}</div>
                  <div style={{ fontSize: 12, wordBreak: 'break-all', marginTop: 4 }}>{String(entryView.value ?? '—')}</div>
                </Alert>
              )}
            </>
          )}
        </>
      )}

      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm ? confirm.title : ''}
        body={confirm ? confirm.body : ''}
        okLabel={deleting ? 'Deleting…' : 'Delete'}
        danger
        onOk={() => { if (confirm) confirm.onOk() }}
      />
    </>
  )
}
